import React, { useMemo } from 'react';
import { getISTMidnightTimestamp } from '../hooks/useFirebaseData';

const ONLINE_THRESHOLD_MS = 3 * 60 * 1000; // 3 minutes — matches the old KioskStatusPanel's rule

function aggregateForKiosk(orders, refundedIds, kioskId) {
  let revenue = 0;
  let cups = 0;
  orders.forEach((order) => {
    if (order.kiosk_id !== kioskId) return;
    if (refundedIds.has(order.id)) return;
    if (order.amount) revenue += Number(order.amount);
    if (order.item_id) cups += 1;
    if (!order.amount && order.status === 'PAID') {
      revenue += 19;
      cups += 1;
    }
  });
  return { revenue, cups };
}

export default function FleetOverviewGrid({ telemetry, inventory, orders, refundedIds, onSelectKiosk }) {
  const kioskIds = useMemo(() => {
    const ids = new Set([...Object.keys(inventory || {}), ...Object.keys(telemetry || {})]);
    return Array.from(ids).sort();
  }, [inventory, telemetry]);

  const todayOrders = useMemo(() => {
    const cutoff = getISTMidnightTimestamp();
    return orders.filter((o) => (o.timestamp || 0) >= cutoff);
  }, [orders]);

  if (kioskIds.length === 0) {
    return (
      <div className="section">
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-title-icon">🖥️</span>
            Fleet Overview
          </h2>
        </div>
        <p style={{ color: 'var(--text-muted)', padding: '1rem 0' }}>
          No kiosks reporting yet. A kiosk appears here once it's sent inventory or telemetry data.
        </p>
      </div>
    );
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2 className="section-title">
          <span className="section-title-icon">🖥️</span>
          Fleet Overview
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: 0 }}>
          Click a kiosk to see its individual data
        </p>
      </div>

      <div className="kpi-grid" style={{ marginTop: '0.5rem' }}>
        {kioskIds.map((kioskId) => {
          const data = (telemetry || {})[kioskId] || {};
          const lastSeen = data.last_seen ? new Date(data.last_seen) : null;
          const msSince = lastSeen ? Date.now() - lastSeen.getTime() : Infinity;
          const isOnline = msSince < ONLINE_THRESHOLD_MS;

          const minutesAgo = lastSeen ? Math.floor(msSince / 60000) : null;
          const lastSeenLabel = minutesAgo === null ? 'Never' : minutesAgo < 1 ? 'Just now' : `${minutesAgo}m ago`;

          const { revenue, cups } = aggregateForKiosk(todayOrders, refundedIds, kioskId);

          return (
            <div
              key={kioskId}
              id={`fleet-card-${kioskId}`}
              className="kpi-card"
              role="button"
              tabIndex={0}
              onClick={() => onSelectKiosk(kioskId)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSelectKiosk(kioskId); }}
              style={{
                borderLeft: `3px solid ${isOnline ? 'var(--accent-green)' : '#ff4444'}`,
                cursor: 'pointer',
              }}
            >
              <div className="kpi-card-header">
                <span
                  className="kpi-card-icon"
                  style={{
                    backgroundColor: isOnline ? 'var(--accent-green-glow)' : 'rgba(255,68,68,0.15)',
                    color: isOnline ? 'var(--accent-green)' : '#ff4444',
                    fontSize: '1.1rem',
                  }}
                >
                  {isOnline ? '🟢' : '🔴'}
                </span>
                <span className="kpi-card-label">{kioskId}</span>
              </div>

              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ color: isOnline ? 'var(--accent-green)' : '#ff4444', fontWeight: 700, fontSize: '1.1rem' }}>
                  {isOnline ? 'ONLINE' : 'OFFLINE'}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  State: <strong style={{ color: 'var(--text-primary)' }}>{data.state || '—'}</strong>
                </div>
                {data.temperature > -990 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    Temp: <strong style={{ color: data.temperature > 30 ? '#ffb347' : 'var(--accent-blue)' }}>
                      {data.temperature.toFixed(1)}°C
                    </strong>
                  </div>
                )}
                <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  Last seen: {lastSeenLabel}
                </div>
                <div style={{
                  marginTop: '0.5rem', paddingTop: '0.5rem',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem',
                }}>
                  <span style={{ color: 'var(--text-muted)' }}>Today: <strong style={{ color: 'var(--accent-blue)' }}>{cups} cups</strong></span>
                  <span style={{ color: 'var(--accent-green)', fontWeight: 700 }}>₹{revenue.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
